import React, { useState } from 'react';
import { Box, Divider, Typography, styled, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import StyledButton from '../Common/StyledButton';
import UploadCSV from './UploadCSV';
import LabeledInput from '../Common/LabeledInput';
import CustomDatePicker from '../DatePicker/DatePicker';
import CustomPhoneNumberInput from '../CustomPhoneNumberInput/CustomPhoneNumberInput';
import scrollbarStyles from '../../utils/styles';
import { IGroupClientFormValues, IMember } from '../../utils/validations/clientDetailsForm';
import { sanitizeInputName } from '../../utils/userInputUtils';
import { useAppSelector } from '../../redux/hooks';

const StyledOutlinedButton = styled(Box)(() => ({
  padding: '12px 32px',
  borderRadius: '15px',
  border: '1.5px solid var(--Brand-Cadet-Blue, #6599A5)',
  fontFamily: 'Satoshi',
  fontSize: '15px',
  fontWeight: 700,
  lineHeight: '20px',
  minWidth: '100px',
  textAlign: 'center',
  display: 'inline-block',
  cursor: 'pointer',
  alignContent: 'center',
}));

interface IAddClientGroupMembersDataGridProps {
  formik: FormikProps<IGroupClientFormValues>;
  onPrev: () => void;
  onAddGroup: (_values: IGroupClientFormValues) => void;
}

export default function AddClientGroupMembersDataGrid({
  formik,
  onPrev,
  onAddGroup,
}: IAddClientGroupMembersDataGridProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const addClientLoading = useAppSelector((state) => state.client.addClientLoading);

  const handleAddRow = () => {
    let updatedMembers = [...formik.values.members];
    updatedMembers = updatedMembers.filter(
      (member) => member.name || member.dob || member.email || member.phoneNumber
    );
    const newMember = { name: '', dob: '', email: '', phoneNumber: '' };
    formik.setFieldValue('members', [...updatedMembers, newMember]);
  };

  const handleDeleteRow = (index: number) => {
    const updatedMembers = formik.values.members.filter((_, i) => i !== index);
    formik.setFieldValue('members', updatedMembers);
  };

  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100);

  const handleEmailChange = (index: number, field: string, value: string) => {
    const updatedMembers = [...formik.values.members];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    formik.setFieldValue('members', updatedMembers);
  };

  const handleDateChange = (field: string, value: string, index: number) => {
    const updatedMembers = [...formik.values.members];
    updatedMembers[index] = { ...updatedMembers[index], dob: value };
    formik.setFieldValue('members', updatedMembers);
  };

  const handleNameChange = (index: number, value: string) => {
    const updatedMembers = [...formik.values.members];
    updatedMembers[index] = { ...updatedMembers[index], name: sanitizeInputName(value) };
    formik.setFieldValue('members', updatedMembers);
  };

  const handlePhoneNumberChange = (index: number, value: string) => {
    const updatedMembers = [...formik.values.members];
    updatedMembers[index] = { ...updatedMembers[index], phoneNumber: value };
    formik.setFieldValue('members', updatedMembers);
  };

  const handleUploadCSV = () => {
    setIsModalOpen(true);
  };

  const addButtonDisabled = formik.values.members.length === 0;

  const handleSubmitMembers = () => {
    onAddGroup(formik.values);
  };

  const getFieldError = (field: string, index: number) =>
    formik.touched.members?.[index]?.[field] && formik.errors.members?.[index]?.[field];

  const renderInputRow = (member: IMember, index: number) => (
    <Grid
      key={index}
      container
      spacing={1}
      pb={2}
      alignItems="flex-start"
      flexDirection="row"
      sx={{ position: 'relative' }}
    >
      <Grid item sm={2.8} mb={getFieldError('name', index) ? -2 : 0}>
        <LabeledInput
          label=""
          id={`name-${index}`}
          type="text"
          value={member.name}
          handleChange={(e) => handleNameChange(index, e.target.value)}
          onBlur={formik.handleBlur}
          placeholder={t('addNewClient.name')}
          error={formik.touched.members?.[index]?.name && formik.errors.members?.[index]?.name}
        />
      </Grid>
      <Grid item sm={2.8} mb={getFieldError('name', index) ? -2 : 0}>
        <CustomDatePicker
          key={`dob-${index}`}
          label=""
          handleOneTimeChange={(field, value) => handleDateChange(field, value, index)}
          minDate={minDate.toISOString().split('T')[0]}
          maxDate={new Date().toISOString().split('T')[0]}
          date={member.dob || ''}
          error={formik.touched.members?.[index]?.dob && formik.errors.members?.[index]?.dob}
        />
      </Grid>
      <Grid item sm={2.8} mb={getFieldError('name', index) ? -2 : 0}>
        <LabeledInput
          label=""
          id={`email-${index}`}
          type="email"
          value={member.email}
          handleChange={(e) => handleEmailChange(index, 'email', e.target.value)}
          onBlur={formik.handleBlur}
          placeholder={t('addNewClient.email')}
          error={formik.touched.members?.[index]?.email && formik.errors.members?.[index]?.email}
        />
      </Grid>
      <Grid item sm={2.8} mt={0.58} mb={getFieldError('name', index) ? -2 : 0}>
        <CustomPhoneNumberInput
          value={member.phoneNumber}
          handleChange={(value) => handlePhoneNumberChange(index, value)}
          onBlur={formik.handleBlur}
          labelRequired={false}
          error={
            formik.touched.members?.[index]?.phoneNumber &&
            formik.errors.members?.[index]?.phoneNumber
          }
        />
      </Grid>
      <Grid
        item
        sm={0.5}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => handleDeleteRow(index)}
      >
        <img src="/assets/icons/close-circle-chip.svg" alt="close-icon" width={24} height={24} />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Header Row */}
      <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Grid item sm={2.8}>
          <Typography variant="meta" color="primary" align="center">
            {t('addNewClient.name')}
          </Typography>
        </Grid>
        <Grid item sm={2.8}>
          <Typography variant="meta" color="primary" align="center">
            {t('addNewClient.birthday')}
          </Typography>
        </Grid>
        <Grid item sm={2.8}>
          <Typography variant="meta" color="primary" align="center">
            {t('addNewClient.email')}
          </Typography>
        </Grid>
        <Grid item sm={2.8}>
          <Typography variant="meta" color="primary" align="center">
            {t('addNewClient.tp')}
          </Typography>
        </Grid>
        <Grid item sm={0.5}>
          <Typography variant="meta" color="primary" align="center">
            {/* Empty space for close icon */}
          </Typography>
        </Grid>
      </Grid>

      {/* Dynamic Rows */}
      <Grid
        container
        spacing={2}
        pb={2}
        px={1}
        maxHeight={200}
        sx={{ overflowX: 'hidden', overflowY: 'scroll', ...scrollbarStyles }}
      >
        {formik.values.members.map(renderInputRow)}

        {/* Add Row Button */}
        <Box
          sx={{
            width: '100%',
            height: '52px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px dashed #ABD1C6',
            borderRadius: '5px',
            padding: '12px',
            gap: '16px',
            cursor: 'pointer',
            mt: 2,
          }}
          onClick={handleAddRow}
        >
          <img src="/assets/icons/plus-button.svg" alt="plus-icon" width={28} height={28} />
        </Box>
      </Grid>

      {/* Footer Buttons */}
      <Divider sx={{ my: 2 }} />
      <Box display="flex" flexDirection="row" justifyContent="space-between" p={1}>
        <StyledOutlinedButton onClick={onPrev}>{t('addNewClient.back')}</StyledOutlinedButton>
        <Box display="flex" flexDirection="row" gap="8px">
          <StyledOutlinedButton onClick={handleUploadCSV}>
            {t('addNewClient.uploadCSV')}
          </StyledOutlinedButton>
          <StyledButton
            disabled={addButtonDisabled}
            onClick={handleSubmitMembers}
            loading={addClientLoading}
          >
            {t('addNewClient.addGroup')}
          </StyledButton>
        </Box>
      </Box>

      {/* Upload CSV Modal */}
      <UploadCSV isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} formik={formik} />
    </Box>
  );
}
